/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertColor,
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash';

import {
  convertDefaultValue,
  extractFacilityIds,
  getStoredCustomerDetails,
  isSuccessful,
} from '../../../../../Shared/Utilities/utils';
import { Patterns } from '../../../../../Shared/Constants';
import {
  geoFenceRadiusInMiles,
  mileInMeters,
  Paths,
  timeoutValue,
  SnackbarSeverity,
  Types,
  DefaultLength,
} from '../../../../../Shared/Constants/App';

import AllJobsApiService from '../../../../Jobs/Services/JobManagementService';
import { ApiResponse, FacilityCoordinates, GeoFence, SyncFacilityCoordinates, SelectedJob } from '../AllJobs.Model';

import PageTitle from '../../../../../Shared/Components/Common/PageTitle/PageTitle';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import ModifyPopUp from '../../../../Jobs/Components/JobDetails/ModifyPopUp';
import SnackbarMessage from '../../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';

import { CustomerContainer } from '../../../../../Shared/Components/Layout/styles';
import { wrapperStyles } from '../../AllSchedules/AllJobs.Style';
import { FieldWrapper } from '../../../../Customer/Components/AddUser/AddUser.Style';
import { buttonStyles } from '../../../../Jobs/Components/JobDetails/OneTimeJobDetails.Style';
import { ErrorMsg } from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import {
  CancelButtonWrapper,
  FacilityCoordinatesContainer,
  RadioLabel,
  RadioWrapper,
  SaveButtonContainer,
  SliderContainer,
  TurnGeoFenceOffContainer,
  TurnGeoFenceOnContainer,
} from './ModifyGeoFence.Style';
import theme from '../../../../../Shared/Themes/theme';
import MyLocationIcon from '@mui/icons-material/MyLocation';

const ModifyGeoFence: React.FC = () => {
  const { t } = useTranslation(['jobManagement', 'assignJob']);
  const { radioButton, radioButtonText, radioButtonDisabled } = wrapperStyles;
  const { textField } = buttonStyles;
  const location = useLocation();
  const selectedJobs: SelectedJob[] = location?.state?.selectedRows;
  const isEditLatLng = location?.state?.editLatLng;
  const [selectedOption, setSelectedOption] = useState<GeoFence | ''>('');
  const initialSliderValue = convertDefaultValue(
    typeof selectedJobs?.[0]?.geoFenceRadius === Types.Number ? Number(selectedJobs?.[0]?.geoFenceRadius) : 0
  );
  const [sliderValue, setSliderValue] = useState<number>(initialSliderValue);
  const [isOpenConfirmPopUp, setIsOpenConfirmPopUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenTurnOffPopUp, setIsOpenTurnOffPopup] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>(SnackbarSeverity.success);
  const [disableGeoFenceOff, setDisableGeoFenceOff] = useState(false);
  const [hasGeoFenceOn, setHasGeoFenceOn] = useState(false);

  const { control, setValue, setError, watch, getValues, clearErrors } = useForm({
    defaultValues: {
      latitude: location.state?.selectedRows?.[0]?.latitude ?? '',
      longitude: location.state?.selectedRows?.[0]?.longitude ?? '',
    },
  });

  const watchFields = watch();
  const { userId } = getStoredCustomerDetails() || {};
  const navigate = useNavigate();

  const handleOptionChange = (event: { target: { value: React.SetStateAction<string> } }) => {
    setSelectedOption(event.target.value as GeoFence);
  };

  const handleSaveButton = () => {
    setIsOpenConfirmPopUp(true);
  };

  const handleCancelGeoFenceOnButton = () => {
    setSelectedOption(GeoFence.OFF);
    setDisableGeoFenceOff(true);
  };

  const handleCancelGeoFenceOffButton = () => {
    setSelectedOption(GeoFence.ON);
    setDisableGeoFenceOff(false);
  };
  const handleCloseConfirmPopUp = () => {
    setIsOpenConfirmPopUp(false);
  };

  const showSnackbar = (severity: AlertColor, message: string) => {
    setSnackbarOpen(true);
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
  };

  const displaySnackBars = async (
    response: ApiResponse,
    manageFacilityCoordinates: ApiResponse<{ isSuccess: boolean }>,
    syncFacilityCoordinatesPayload: SyncFacilityCoordinates[]
  ) => {
    const geoFenceSuccess = isSuccessful(response);

    const coordinatesSuccess = isSuccessful(manageFacilityCoordinates) && manageFacilityCoordinates?.data?.isSuccess;

    if (geoFenceSuccess && coordinatesSuccess) {
      await AllJobsApiService.syncFacilityCoordinates(syncFacilityCoordinatesPayload, userId);
      showSnackbar(SnackbarSeverity.success, t('jobManagement:geoFenceAndLatLongUpdated'));
      setTimeout(() => {
        setIsLoading(false);
        navigate(Paths.ManageAllJobs);
      }, timeoutValue);
    } else if (geoFenceSuccess && !coordinatesSuccess) {
      showSnackbar(SnackbarSeverity.error, t('jobManagement:geoFenceUpdatedLatLongFailed'));
    } else if (!geoFenceSuccess && coordinatesSuccess) {
      showSnackbar(SnackbarSeverity.error, t('jobManagement:geoFenceFailedLatLongUpdated'));
    } else {
      showSnackbar(SnackbarSeverity.error, t('jobManagement:failedToUpdateBoth'));
    }
  };

  const handleConfirmButton = async () => {
    try {
      setIsLoading(true);
      const uniqueFacilityIds = extractFacilityIds(selectedJobs);
      const valueInMeters = sliderValue * mileInMeters; // This is to convert geoFenceRadius from miles to meters
      const { latitude, longitude } = getValues();
      const syncFacilityCoordinates: SyncFacilityCoordinates[] = selectedJobs.map(
        ({ jobId, jobNumber, workOrderId }) => ({
          jobId,
          jobNumber,
          workOrderId,
          geoRadius: valueInMeters.toString(),
        })
      );
      const response = await AllJobsApiService.updateGeoFenceRadius(uniqueFacilityIds as string[], valueInMeters);

      const shouldUpdateCoordinates =
        (selectedJobs?.length === DefaultLength &&
          (latitude !== selectedJobs?.[0]?.latitude || longitude !== selectedJobs?.[0]?.longitude)) ||
        isEditLatLng;

      if (shouldUpdateCoordinates) {
        const { facilityId, jobId, customerId, workOrderId } = selectedJobs[0];

        const manageFacilityCoordinates = await AllJobsApiService.updateFacilityCoordinates(
          facilityId,
          jobId,
          workOrderId,
          customerId,
          latitude,
          longitude,
          userId
        );

        const syncFacilityCoordinatesPayload: SyncFacilityCoordinates[] = selectedJobs.map(
          ({ jobId, jobNumber, workOrderId }) => ({
            jobId,
            jobNumber,
            workOrderId,
            geoRadius: valueInMeters.toString(),
            geoCoordinates: {
              latitude: latitude,
              longitude: longitude,
            },
          })
        );

        await displaySnackBars(response, manageFacilityCoordinates ?? {}, syncFacilityCoordinatesPayload);
      } else {
        const geoFenceSuccess = isSuccessful(response);

        if (geoFenceSuccess) {
          await AllJobsApiService.syncFacilityCoordinates(syncFacilityCoordinates, userId);

          showSnackbar(SnackbarSeverity.success, t('jobManagement:geoFenceUpdatedSuccessfully'));
          setTimeout(() => {
            setIsLoading(false);
            navigate(Paths.ManageAllJobs);
          }, timeoutValue);
        } else {
          showSnackbar(SnackbarSeverity.error, t('jobManagement:failedToUpdateGeoFence'));
        }
      }
    } catch (error) {
      console.error(t('jobManagement:errorOccurredWhileUpdated'), error);
      showSnackbar(SnackbarSeverity.error, t('jobManagement:errorOccurredWhileUpdated'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenTurnOffPopUp = () => {
    setIsOpenTurnOffPopup(true);
  };

  const handleCloseTurnOffPopUp = () => {
    setIsOpenTurnOffPopup(false);
  };

  const handleTurnOffGeoFence = async () => {
    try {
      setIsLoading(true);
      const uniqueFacilityIds = extractFacilityIds(selectedJobs);
      const valueInMeters = 0;
      const syncFacilityCoordinates: SyncFacilityCoordinates[] = selectedJobs.map(
        ({ jobId, jobNumber, workOrderId }) => ({
          jobId,
          jobNumber,
          workOrderId,
          geoRadius: valueInMeters.toString(),
        })
      );

      const response = await AllJobsApiService.updateGeoFenceRadius(uniqueFacilityIds as string[], valueInMeters);
      const geoFenceSuccess = isSuccessful(response);

      if (geoFenceSuccess) {
        await AllJobsApiService.syncFacilityCoordinates(syncFacilityCoordinates, userId);

        showSnackbar(SnackbarSeverity.success, t('jobManagement:geoFenceUpdatedSuccessfully'));
        setTimeout(() => {
          setIsLoading(false);
          navigate(Paths.ManageAllJobs);
        }, timeoutValue);
      } else {
        showSnackbar(SnackbarSeverity.error, t('jobManagement:failedToUpdateGeoFence'));
      }
    } catch (error) {
      console.error(t('jobManagement:errorOccurredWhileUpdated'), error);
      showSnackbar(SnackbarSeverity.error, t('jobManagement:errorOccurredWhileUpdated'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatValueText = (value: number) => {
    setSliderValue(value);
    return `${value}`;
  };

  const isLatNotInRange = (lat: number) => {
    return !isNaN(lat) && (lat <= -90 || lat >= 90);
  };

  const isLongNotInRange = (long: number) => {
    return !isNaN(long) && (long <= -180 || long >= 180);
  };
  const handleErrorForLatitude = (isError: boolean, message = '') => {
    if (isError) {
      setError(FacilityCoordinates.latitude, { type: 'validate', message });
    } else {
      clearErrors(FacilityCoordinates.latitude);
    }
  };

  const handleErrorForLongitude = (isError: boolean, message = '') => {
    if (isError) {
      setError(FacilityCoordinates.longitude, { type: 'validate', message });
    } else {
      clearErrors(FacilityCoordinates.longitude);
    }
  };

  const disableSaveButton = () => {
    const { latitude, longitude } = getValues();

    const lat = Number(latitude);
    const long = Number(longitude);

    let hasError = false;

    if (isLatNotInRange(lat)) {
      handleErrorForLatitude(true, t('jobManagement:LatitudeRangeCheck'));
      hasError = true;
    } else {
      if (lat === 0) {
        handleErrorForLatitude(true, t('jobManagement:LatitudeCannotBeZero'));
        hasError = true;
      } else {
        handleErrorForLatitude(false);
      }
    }

    if (isLongNotInRange(long)) {
      handleErrorForLongitude(true, t('jobManagement:LongitudeRangeCheck'));
      hasError = true;
    } else {
      if (long === 0) {
        handleErrorForLongitude(true, t('jobManagement:LongitudeCannotBeZero'));
        hasError = true;
      } else {
        handleErrorForLongitude(false);
      }
    }

    return hasError;
  };

  const handleLatLong = () => {
    const lat = location.state?.selectedRows?.[0]?.latitude;
    const lng = location.state?.selectedRows?.[0]?.longitude;
    navigate(`/all-jobs/modify-geofence/add-latlng?lat=${lat}&lng=${lng}&addressExists=true`, {
      state: { ...location.state, modifiedRadius: sliderValue },
    });
  };
  useEffect(() => {
    disableSaveButton();
  }, [watchFields]);

  useEffect(() => {
    if (!isEmpty(location.state?.selectedRows)) {
      setValue(FacilityCoordinates.latitude, location.state?.selectedRows?.[0]?.latitude || '');
      setValue(FacilityCoordinates.longitude, location.state?.selectedRows?.[0]?.longitude || '');
    }
  }, [location.state?.selectedRows]);

  useEffect(() => {
    const radius = location.state?.selectedRows?.[0]?.geoFenceRadius;
    if (location.state?.selectedRows?.length === DefaultLength) {
      if (radius > 0) {
        setSelectedOption(GeoFence.ON);
        setHasGeoFenceOn(true);
      } else {
        setSelectedOption(GeoFence.OFF);
        setDisableGeoFenceOff(true);
      }
    }
  }, []);

  useEffect(() => {
    if (location?.state?.editLatLng) {
      setSelectedOption(GeoFence.ON);
    }
  }, []);

  const handleDefaultValue = () => {
    if (selectedJobs?.length === DefaultLength && selectedJobs[0]?.geoFenceRadius && !location?.state?.editLatLng) {
      const radius = location.state?.selectedRows?.[0]?.geoFenceRadius;
      return convertDefaultValue(typeof radius === Types.Number ? radius : 0);
    }
    const radius = location.state?.geoFenceRadius;
    return convertDefaultValue(typeof radius === Types.Number ? radius : 0);
  };

  return (
    <>
      <PageTitle title={t('jobManagement:modifyGeoFence')} />
      <CustomerContainer>
        <>
          <RadioGroup value={selectedOption} onChange={handleOptionChange} sx={{ gap: '2rem', margin: '3rem 0' }}>
            {disableGeoFenceOff ? (
              <RadioWrapper>
                <Radio
                  checked
                  disabled
                  sx={{
                    ...radioButtonDisabled,
                  }}
                />
                <RadioLabel>{t('jobManagement:geoFenceIsOff')}</RadioLabel>
              </RadioWrapper>
            ) : (
              <FormControlLabel
                value={GeoFence.OFF}
                control={
                  <Radio
                    sx={{
                      ...radioButton,
                      fontSize: theme.typography.h3?.fontSize,
                    }}
                  />
                }
                label={t('jobManagement:turnGeoFenceOff')}
                sx={{
                  ...radioButtonText,
                  '&.MuiFormControlLabel-root': {
                    '&> span:last-child': {
                      fontSize: theme.typography.h3?.fontSize,
                    },
                  },
                }}
              />
            )}
            <FormControlLabel
              value={GeoFence.ON}
              control={
                <Radio
                  sx={{
                    ...radioButton,
                  }}
                />
              }
              label={hasGeoFenceOn ? t('jobManagement:geoFenceIsOn') : t('jobManagement:turnGeoFenceOn')}
              sx={{
                ...radioButtonText,
                '&.MuiFormControlLabel-root': {
                  '&> span:last-child': {
                    fontSize: theme.typography.h3?.fontSize,
                  },
                },
              }}
            />
          </RadioGroup>

          {selectedOption === GeoFence.ON && (
            <TurnGeoFenceOnContainer selectedRowCount={selectedJobs?.length}>
              <Box sx={{ gap: '1rem' }}>
                <SliderContainer>
                  <Slider
                    aria-label={t('jobManagement:geoFenceRadius')}
                    defaultValue={handleDefaultValue()}
                    getAriaValueText={formatValueText}
                    step={0.5}
                    min={0}
                    max={100}
                    valueLabelDisplay="auto"
                    marks={geoFenceRadiusInMiles}
                    sx={{
                      color: theme.palette.primary.dark,
                    }}
                  />
                </SliderContainer>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('jobManagement:selectedRadius')} {sliderValue} {t('jobManagement:miles')}
                </Typography>
              </Box>
              {location?.state?.selectedRows?.length === DefaultLength && (
                <FacilityCoordinatesContainer sx={{ alignItems: 'center' }}>
                  <Controller
                    control={control}
                    name={FacilityCoordinates.latitude}
                    render={({ field: { onChange, value }, fieldState: { error } }) => {
                      return (
                        <FieldWrapper>
                          <TextField
                            sx={textField}
                            label={t('jobManagement:latitude')}
                            value={value}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (Patterns.LatLong.test(val)) {
                                onChange(val);
                              }
                            }}
                          />
                          <ErrorMsg>{error && error?.message}</ErrorMsg>
                        </FieldWrapper>
                      );
                    }}
                  />
                  <Controller
                    control={control}
                    name={FacilityCoordinates.longitude}
                    render={({ field: { onChange, value }, fieldState: { error } }) => {
                      return (
                        <FieldWrapper>
                          <TextField
                            sx={textField}
                            label={t('jobManagement:longitude')}
                            value={value}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (Patterns.LatLong.test(val)) {
                                onChange(val);
                              }
                            }}
                          />{' '}
                          <ErrorMsg>{error && error?.message}</ErrorMsg>
                        </FieldWrapper>
                      );
                    }}
                  />
                  <Box sx={{ marginBottom: '2.5rem' }}>
                    <IconButton onClick={handleLatLong}>
                      <MyLocationIcon sx={{ color: theme.palette.common.black }} />
                    </IconButton>
                  </Box>
                </FacilityCoordinatesContainer>
              )}
              <SaveButtonContainer sx={{ display: 'flex' }}>
                {!hasGeoFenceOn && selectedJobs?.length === DefaultLength && (
                  <CancelButtonWrapper>
                    <Button
                      removeBgColor={theme.palette.secondary.main}
                      type={ButtonType.Secondary}
                      label={t('dashboard:cancel')}
                      onClick={handleCancelGeoFenceOnButton}
                      disabled={disableSaveButton()}
                    />
                  </CancelButtonWrapper>
                )}
                <Button
                  type={ButtonType.Primary}
                  label={t('assignJob:save')}
                  onClick={handleSaveButton}
                  disabled={disableSaveButton()}
                />
              </SaveButtonContainer>
            </TurnGeoFenceOnContainer>
          )}
          {selectedOption === GeoFence.OFF && !disableGeoFenceOff && (
            <TurnGeoFenceOffContainer>
              <SaveButtonContainer sx={{ marginTop: '3rem' }}>
                {hasGeoFenceOn && (
                  <CancelButtonWrapper>
                    <Button
                      removeBgColor={theme.palette.secondary.main}
                      type={ButtonType.Secondary}
                      label={t('dashboard:cancel')}
                      onClick={handleCancelGeoFenceOffButton}
                      disabled={disableSaveButton()}
                    />
                  </CancelButtonWrapper>
                )}
                <Button
                  type={ButtonType.Primary}
                  label={t('assignJob:save')}
                  onClick={handleOpenTurnOffPopUp}
                  disabled={false}
                />
              </SaveButtonContainer>
            </TurnGeoFenceOffContainer>
          )}
        </>
        <ModifyPopUp
          open={isOpenConfirmPopUp}
          handleClose={handleCloseConfirmPopUp}
          handleOnCloseJob={handleConfirmButton}
          heading={''}
          text={t('jobManagement:confirmPopUpText')}
          width={'41.25rem'}
          loading={isLoading}
        />
        <ModifyPopUp
          open={isOpenTurnOffPopUp}
          handleClose={handleCloseTurnOffPopUp}
          handleOnCloseJob={handleTurnOffGeoFence}
          heading={''}
          text={t('jobManagement:turnOffGeoFencePopUp')}
          width={'41.25rem'}
          loading={isLoading}
        />
        <SnackbarMessage
          open={snackbarOpen}
          successMessage={snackbarMessage}
          errorMessage={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />
      </CustomerContainer>
    </>
  );
};

export default ModifyGeoFence;
